<template>
    <div class="btn-group" v-bind="$attrs">
        <BaseButton 
            id="dropdownMenuClickableInside" 
            class="btn--filter" 
            variant="outline" 
            variant-style="secondary"
            aria-expanded="false" 
            v-bind="$attrs"
            data-bs-toggle="dropdown" 
            data-bs-auto-close="true" 
            is-content
        >
            <template #content>
                <span class="flex align-items-center">
                    <IconSvg name="filter" fill="#637579" width="12px" height="12px" />
                    <span class="ml--5">Filter</span>
                </span>
            </template>
        </BaseButton>

        <div class="dropdown-menu filter__dropdown-menu mb--35">
            <form class="filter-form" @submit.prevent="">
                <div class="action--btns__wrap">
                    <button class="action--btns__clear action--btns" type="reset" @click="clearFilter">
                        Clear all
                    </button>
                    <BaseButton 
                        label="Run" 
                        :disabled="disabled" 
                        type="submit" 
                        @click="handleFilterUpdate"
                    />
                </div>

                <div class="mb--15">
                    <div v-if="filter.name.allowedPaths.includes(pageType)" class="filter-item-input">
                        <FormLabel title="Name" />
                        <input id="name" v-model="filter.name.value" class="form-control form-control-sm filter-input" type="text">
                    </div>

                    <div v-if="filter.currency.allowedPaths.includes(pageType)" class="filter-item-input">
                        <FormLabel title="Currency" classes="ml--0" />
                        <input 
                            id="currency" 
                            v-model="filter.currency.value" 
                            class="form-control form-control-sm"                                    
                            type="text"
                        >
                    </div>

                    <div v-if="filter.industry.allowedPaths.includes(pageType)" class="filter-item-input">
                        <FormLabel title="Industry" />
                        <select v-model="filter.industry.value" class="form-select form-select-sm text--sm filter-input text--capitalize">
                            <option v-for="(industry, index) in industryData" :key="index" :value="industry">
                                {{ industry }}
                            </option>
                        </select>
                    </div>

                    <div v-if="filter.status.allowedPaths.includes(pageType)" class="filter-item-input">
                        <FormLabel title="Status" />
                        <div>
                            <div v-for="status in statusList" :key="status" class="form-check form-check-inline">
                                <input
                                    :id="`status-${status}`" 
                                    v-model="filter.status.value" 
                                    class="form-check-input" 
                                    type="checkbox"
                                    :value="status"
                                >
                                <label class="form-check-label text--capitalize" :for="`status-${status}`">{{ status }}</label>
                            </div>
                        </div>
                    </div>

                    <div v-if="filter.title.allowedPaths.includes(pageType)" class="filter-item-input">
                        <FormLabel title="Title" />
                        <input id="projectTitle" v-model="filter.title.value" class="form-control form-control-sm filter-input" type="text">
                    </div>

                    <div v-if="filter.projectType.allowedPaths.includes(pageType)" class="filter-item-input">
                        <FormLabel title="Project type" />
                        <select v-model="filter.projectType.value" class="form-select form-select-sm text--sm filter-input text--capitalize">
                            <option v-for="status in ['paid', 'not paid']" :key="status" :value="status" class="text--capitalize">
                                {{ status }}
                            </option>
                        </select>
                    </div>

                    <div v-if="filter.rateType.allowedPaths.includes(pageType)" class="filter-item-input">
                        <FormLabel title="Rate card type" />
                        <div>
                            <div v-for="item in rateTypes" :key="item" class="form-check form-check-inline">
                                <input
                                    :id="`check-${item}`" v-model="filter.rateType.value" :value="item" class="form-check-input"
                                    type="checkbox"
                                >
                                <label class="form-check-label text--capitalize" :for="`check-${item}`">{{ item }}</label>
                            </div>
                        </div>
                    </div>

                    <div v-if="filter.invoiceNo.allowedPaths.includes(pageType)" class="filter-item-input">
                        <FormLabel title="Invoice No." />
                        <input id="invoiceNo" v-model="filter.invoiceNo.value" class="form-control form-control-sm" type="text">
                    </div>
                </div>
            </form>
        </div>
    </div>
</template>

<script>
import { 
    projectFilterStatuses, 
    priorities, 
    taskStatus, 
    invoiceFilterStatuses, 
    clientFilterStatuses,
    MEETING_STATUSES, 
} from "@/utils/enums";
import { industryData, rateTypes } from "@/utils/dummy";
import { isFilterValueEmpty } from "@/utils/helpers";
import FormLabel from "../forms/FormLabel.vue";
import IconSvg from "../icons/Icon-Svg.vue";
import BaseButton from "../buttons/BaseButton.vue";

export default {
    name: "InnerPageFilter",

    components: {
        FormLabel,
        IconSvg, 
        BaseButton,
    },

    props: {
        modelValue: { type: [String, Object, Array], default: undefined },
        pageType: { type: String, default: "" },
    },

    emits: ["update:modelValue", "update-filter", "clear"],

    data() {
        return {
            projectFilterStatuses,
            priorities,
            taskStatus,
            invoiceFilterStatuses,
            clientFilterStatuses,
            industryData,
            filter: {
                ...this.getInitialFilter(),
                ...this.modelValue,
            },
            minEndDate: null,
            rateTypes,
            initialCurrencyList: Object.values(this.$currencies_obj),
            selectedCurrency: {},
            currencyMap: this.$supported_currencies_map,
            MEETING_STATUSES,
        };
    },

    computed: {
        statusList() {
            switch (this.pageType) {
            case "projects":
                return this.projectFilterStatuses;
                
            case "invoices":
                return this.invoiceFilterStatuses;

            case "client_meetings":
                return this.MEETING_STATUSES;
                    
            case "clients":
                return this.clientFilterStatuses;
                    
            case "tasks":
                return this.taskStatus;
                
            default:
                return [];
            }
        },

        disabled() {
            return Object.values(this.filter).some(x => !x);
        },
    },

    watch: {
        "filter.startDate.value": {
            handler(newVal) {
                if (newVal && newVal !== oldVal) {
                    this.minEndDate = new Date(newVal);
                }
            },
        },

        modelValue: {
            deep: true,
            handler(newValue) {                
                this.filter = {
                    ...this.getInitialFilter(),
                    ...newValue,
                };
            },
        },
            
        // Two-way binding: emit changes to parent
        // filter: {
        //     deep: true,
        //     handler(newValue) {
        //         this.$emit("update:modelValue", newValue);
        //     },
        // },
    },
    
    methods: {
        getInitialFilter() {
            const storedFilter = localStorage.getItem("i_f"); // inner filter
            const deserializedFilter = typeof storedFilter === "string" && storedFilter !== "undefined" ? JSON.parse(storedFilter) : {};

            return {
                rateType: { allowedPaths: ["rateCards"], value: deserializedFilter?.rateType || [] },
                status: { allowedPaths: ["projects", "invoices", "clients"], value: deserializedFilter?.status || [] },
                name: { allowedPaths: ["clients"], value: deserializedFilter?.name || "" },
                industry: { allowedPaths: ["clients"], value: deserializedFilter?.industry || "" },
                title: {  allowedPaths: ["projects", "client_meetings"], value: deserializedFilter?.value || "" },
                status: { allowedPaths: ["projects", "clients", "invoices", "client_meetings"], value: deserializedFilter?.status || [] },
                invoiceNo: { allowedPaths: ["invoices"], value: deserializedFilter?.invoiceNo || "" },
                currency: { allowedPaths: ["invoices"], value: deserializedFilter?.currency || "" },
                projectType: { allowedPaths: ["projects"], value: deserializedFilter?.projectType || "" },
            };
        },

        clearFilter() {
            this.localFilter = this.getInitialFilter();
            localStorage.removeItem("i_f");
            this.$emit("clear", {});
        },

        deserializeQueryParam(str) {            
            const [key, encodedValue] = str.split("=");
            const decodedValue = decodeURIComponent(encodedValue);
            
            return {
                [key]: decodedValue,
            };
        },

        handleFilterUpdate() {
            const preparedFilter = { ...this.filter };
            const modifiedParams = {};
                
            Object.entries(preparedFilter).forEach(([key, values]) => {                    
                if (key && !isFilterValueEmpty(values.value)) {
                    modifiedParams[key] = values.value;
                }
            });
            
            // Emit the prepared stringified filter
            localStorage.setItem("i_f", JSON.stringify(modifiedParams));
            this.$emit("update-filter", modifiedParams);
        },

        removeStatusItem(item) {
            filter.status = filter.status.filter(x => x?._id !== item?._id);
        },

        searchCurrencies(val) {
            const initialList = this.initialCurrencyList;

            if (val && typeof val === "string") {
                const filteredList = initialList.filter(item => item.toLowerCase().includes(val.toLowerCase()));
                this.initialCurrencyList = filteredList;
            } else {
                this.initialCurrencyList = Object.values(this.$currencies_obj);
            }
        },

        selectCurrency(item) {
            const index = item && item.toLowerCase() || "";

            this.selectedCurrency = this.currencyMap[index];
            this.filter.currency = item;
        },
    },
};
</script>

<style lang="scss" scoped>
    .action--btns__wrap {
        border-bottom: 1px solid #eee;
        padding-bottom: 10px;
        padding-top: 10px;
        background-color: #f6f8fa !important;
        width: 100%;
        position: sticky;
        top: 0;
        z-index: 10;
    }

    .dropdown-menu.show {
        transform: translate3d(-10px, 30px, 0px) !important;
        transform: translate3d(0px, 30px, 0px) !important;
        width: 300px !important;
        inset: 0px 0px auto auto !important;
        padding: 0px !important;
        box-shadow: inset 0 -1px rgba(235, 238, 241, 0) !important; 
        border-radius: 5px;
   }

   .filter {
        &-input {
            font-size: 12px !important;
            border-radius: 6px;
        }

        &-item-input {
            display: flex;
            flex-direction: column;
            padding: 10px 11px;

            &:not(:last-of-type) {
                border-bottom: 1px solid #eee;
            }

            &:last-of-type {
                padding-bottom: 0px;
            }

            label {
                font-size: 12px !important;
            }
        }

        &-status {
            &-item {
                flex-direction: row !important;
                flex-wrap: wrap;

                label {
                    margin-bottom: 0px !important;
                }
            }
        }

        &-form {
            max-height: 500px;
            overflow-y: auto;
        }
   }
</style>